import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, View, Text, StyleSheet, ScrollView } from 'react-native';
import { StatsUsageService } from '../../../services/stats';
import { AccountPracInfoService } from '../../../services/account/AccountPracInfoService';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { AdminAssessmentStorage } from '../../../services/AdminAssessmentStorage';
import type { AccountSiteDto, StatsUsageRequestDto, StatsUsageResultDto } from '../../../types/api';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { resolveAuthContext } from '../../../services/auth/authContext';
import { AccountFilterSelect } from '../AccountFilterSelect';
import {
  PERIOD_OPTIONS,
  PERIOD_UNIT_LABEL,
  pluralizeUnit,
  PREVIOUS_VALUES,
  DEFAULT_PERIOD,
  DEFAULT_PREVIOUS,
  formatPeriodLabel,
} from './periodFilterOptions';

interface UsageViewProps {
  storage?: any;
  apiService?: any;
  accountId?: string | null;
}

// A Site value of '0' from stats_usage_get means "online admins" (not tied to any one site) —
// displayed as a fixed "Online" label rather than a raw '0'.
const ONLINE_ADMINS_SITE_VALUE = '0';
const ONLINE_ADMINS_SITE_LABEL = 'Online';

interface UsageRow {
  key: string;
  assessment: string;
  site: string;
  period: string;
  total: string;
  /** A blank separator row rendered between one assessment's results and the next. */
  isSpacer?: boolean;
}

const resolveUserStore = (): any => resolveAuthContext().userStore || null;

export const UsageView: React.FC<UsageViewProps> = ({
  storage = null,
  apiService = null,
  accountId: accountIdProp = null,
}) => {
  // The Account filter is a draft (selectedAccountId) until Refresh/period/previous is used —
  // only then does it become the account usage is loaded for (accountId).
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [appliedAccountId, setAppliedAccountId] = useState<string | null>(null);
  const accountId = appliedAccountId ?? accountIdProp;
  const filterAccountId = selectedAccountId ?? accountId;
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [previous, setPrevious] = useState(DEFAULT_PREVIOUS);
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [siteList, setSiteList] = useState<AccountSiteDto[]>([]);

  const statsUsageService = useMemo(
    () => (apiService ? new StatsUsageService(apiService) : null),
    [apiService]
  );
  const accountPracInfoService = useMemo(
    () => (apiService ? new AccountPracInfoService(apiService) : null),
    [apiService]
  );
  const adminAssessmentStorage = useMemo(
    () => (storage ? new AdminAssessmentStorage(storage) : null),
    [storage]
  );

  // Site display names come from account info (SiteList), not from stats_usage_get itself —
  // read whatever's already cached immediately, then refresh from the backend in the
  // background so a site renamed since the last Account visit still shows up-to-date.
  useEffect(() => {
    let cancelled = false;

    const applySiteList = (nextSiteList: AccountSiteDto[] | undefined | null) => {
      if (!nextSiteList || cancelled) {
        return;
      }
      setSiteList(nextSiteList);
    };

    const loadSites = async () => {
      if (!accountId) {
        return;
      }

      if (storage) {
        try {
          const pracStorage = new AccountPracInfoStorage(storage);
          const cached = await pracStorage.getAccountPracInfo(accountId);
          applySiteList(cached?.SiteList);
        } catch (cacheError) {
          console.warn('UsageView: failed to read cached site list', cacheError);
        }
      }

      if (!accountPracInfoService) {
        return;
      }

      try {
        const dto = await accountPracInfoService.getAccountPracInfo(
          { AccountId: accountId, Types: ['AccountSras'] },
          { userStore: resolveUserStore(), storage }
        );

        if (cancelled) {
          return;
        }

        applySiteList(dto.SiteList);

        if (storage) {
          const pracStorage = new AccountPracInfoStorage(storage);
          const latestCached = await pracStorage.getAccountPracInfo(accountId);
          const merged = latestCached
            ? { ...latestCached, SiteList: dto.SiteList, SraList: dto.SraList ?? latestCached.SraList }
            : dto;
          await pracStorage.saveAccountPracInfo(accountId, merged);
        }
      } catch (fetchError) {
        console.warn('UsageView: failed to refresh account site list', fetchError);
      }
    };

    void loadSites();

    return () => {
      cancelled = true;
    };
  }, [accountId, accountPracInfoService, storage]);

  const resolveAssessmentName = useCallback(async (assessmentId: string): Promise<string> => {
    const trimmed = (assessmentId || '').trim();
    if (!trimmed || !adminAssessmentStorage) {
      return assessmentId;
    }
    try {
      const assessment = await adminAssessmentStorage.getAssessmentBySourceId(trimmed);
      return assessment?.dName?.trim() || assessmentId;
    } catch (lookupError) {
      console.warn('UsageView: failed to resolve assessment name', lookupError);
      return assessmentId;
    }
  }, [adminAssessmentStorage]);

  const siteNameById = useMemo(() => {
    const map: Record<string, string> = {};
    siteList.forEach((site) => {
      const id = (site.SiteId || '').trim();
      if (!id) {
        return;
      }
      map[id] = (site.SiteDName || '').trim() || id;
    });
    return map;
  }, [siteList]);

  const resolveSiteName = useCallback((siteId: string): string => {
    const trimmed = (siteId || '').trim();
    if (!trimmed) {
      return siteId;
    }

    // Site '0' means "online admins" — not a real site, shown as a fixed "Online" label.
    if (trimmed === ONLINE_ADMINS_SITE_VALUE) {
      return ONLINE_ADMINS_SITE_LABEL;
    }

    return siteNameById[trimmed] || trimmed;
  }, [siteNameById]);

  const fetchUsage = useCallback(async (nextPeriod: string, nextPrevious: string, targetAccountId: string | null) => {
    if (!targetAccountId) {
      setError('No account selected.');
      return;
    }
    if (!statsUsageService) {
      setError('Stats service unavailable.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request: StatsUsageRequestDto = {
        AccountId: targetAccountId,
        Filters: [
          { Name: 'period', Value: nextPeriod },
          { Name: 'previous', Value: nextPrevious },
        ],
      };

      const response = await statsUsageService.getUsage(request, {
        userStore: resolveUserStore(),
        storage,
      });

      // Assessment display names are looked up per distinct AssessmentId — cached locally
      // within this fetch so a response with many rows for the same assessment doesn't repeat
      // the same content-storage lookup.
      const assessmentNameById: Record<string, string> = {};
      const nextRows: UsageRow[] = [];
      let hasPrecedingGroup = false;

      for (const assessment of response.Assessments ?? []) {
        const results = assessment.Results ?? [];
        if (results.length === 0) {
          continue;
        }

        const assessmentId = assessment.AssessmentId || '';
        if (!(assessmentId in assessmentNameById)) {
          assessmentNameById[assessmentId] = await resolveAssessmentName(assessmentId);
        }
        const assessmentName = assessmentNameById[assessmentId];

        // A blank row separates each assessment's block of results from the next, so it's
        // inserted before a group rather than after — that way there's never a trailing blank
        // row after the last group with data.
        if (hasPrecedingGroup) {
          nextRows.push({ key: `spacer:${assessmentId}`, isSpacer: true, assessment: '', site: '', period: '', total: '' });
        }
        hasPrecedingGroup = true;

        results.forEach((result: StatsUsageResultDto, index: number) => {
          nextRows.push({
            key: `${assessmentId}:${result.Site}:${result.Period}:${index}`,
            // Shown once per assessment (on its first row) rather than repeated down every row.
            assessment: index === 0 ? assessmentName : '',
            site: resolveSiteName(result.Site),
            period: formatPeriodLabel(nextPeriod, result.Period),
            total: result.Total,
          });
        });
      }

      setRows(nextRows);
    } catch (fetchError) {
      const message = fetchError instanceof Error ? fetchError.message : String(fetchError);
      setError(message);
      setRows([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [statsUsageService, storage, resolveAssessmentName, resolveSiteName]);

  // Load usage once an account and the stats service are available — period/previous changes
  // are handled by their own onChange handlers below so this effect intentionally doesn't
  // depend on them (that would double-fetch: once from the handler, once from this effect).
  // Keyed on the prop (not the filter) so picking an Account filter value never loads by itself;
  // a new active account from outside discards any filter pick.
  useEffect(() => {
    setSelectedAccountId(null);
    setAppliedAccountId(null);
    if (accountIdProp && statsUsageService) {
      void fetchUsage(period, previous, accountIdProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountIdProp, statsUsageService]);

  // Period/Previous/Refresh all load for the account currently chosen in the Account filter.
  const loadForFilter = (nextPeriod: string, nextPrevious: string) => {
    setAppliedAccountId(filterAccountId);
    void fetchUsage(nextPeriod, nextPrevious, filterAccountId);
  };

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    loadForFilter(value, previous);
  };

  const handlePreviousChange = (value: string) => {
    setPrevious(value);
    loadForFilter(period, value);
  };

  const handleRefresh = () => {
    loadForFilter(period, previous);
  };

  const isWeb = Platform.OS === 'web';
  const webSelectStyle = { ...webControlStyle, minWidth: 130 } as any;
  const previousSelectStyle = { ...webControlStyle, minWidth: 150 } as any;

  const cyclePeriod = () => {
    const currentIndex = PERIOD_OPTIONS.findIndex((option) => option.value === period);
    const nextIndex = currentIndex < 0 || currentIndex === PERIOD_OPTIONS.length - 1 ? 0 : currentIndex + 1;
    handlePeriodChange(PERIOD_OPTIONS[nextIndex].value);
  };

  const cyclePrevious = () => {
    const currentIndex = PREVIOUS_VALUES.indexOf(previous);
    const nextIndex = currentIndex < 0 || currentIndex === PREVIOUS_VALUES.length - 1 ? 0 : currentIndex + 1;
    handlePreviousChange(PREVIOUS_VALUES[nextIndex]);
  };

  const periodLabel = PERIOD_OPTIONS.find((option) => option.value === period)?.label ?? '';
  const periodUnit = PERIOD_UNIT_LABEL[period] ?? '';
  const previousOptions = useMemo(
    () => PREVIOUS_VALUES.map((value) => ({ value, label: pluralizeUnit(Number(value), periodUnit) })),
    [periodUnit]
  );
  const previousLabel = pluralizeUnit(Number(previous), periodUnit);

  return (
    <View style={styles.panel}>
      <View style={styles.filterBarRow}>
        <View style={styles.filterBar}>
          <Text style={styles.filterLabel}>Account</Text>
          <AccountFilterSelect
            value={filterAccountId}
            onChange={(id) => setSelectedAccountId(id)}
            disabled={isLoading}
          />
        </View>
      </View>

      <View style={styles.filterBarRow}>
        <View style={styles.filterBar}>
          <Text style={styles.filterLabel}>Period</Text>
          {isWeb ? (
            <select
              style={webSelectStyle}
              value={period}
              onChange={(event: any) => handlePeriodChange(String(event?.target?.value ?? DEFAULT_PERIOD))}
              aria-label="Period"
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : (
            <Pressable style={styles.dropdownBtn} onPress={cyclePeriod} accessibilityRole="button" accessibilityLabel="Period">
              <Text style={styles.dropdownBtnText}>{periodLabel}</Text>
              <Text style={styles.dropdownBtnChevron}>▼</Text>
            </Pressable>
          )}

          <View style={styles.filterSpacer} />

          <Text style={styles.filterLabel}>Previous</Text>
          {isWeb ? (
            <select
              style={previousSelectStyle}
              value={previous}
              onChange={(event: any) => handlePreviousChange(String(event?.target?.value ?? DEFAULT_PREVIOUS))}
              aria-label="Previous"
            >
              {previousOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : (
            <Pressable style={styles.dropdownBtn} onPress={cyclePrevious} accessibilityRole="button" accessibilityLabel="Previous">
              <Text style={styles.dropdownBtnText}>{previousLabel}</Text>
              <Text style={styles.dropdownBtnChevron}>▼</Text>
            </Pressable>
          )}

          <View style={styles.filterSpacer} />

          <Pressable
            style={[styles.refreshButton, isLoading && styles.refreshButtonDisabled]}
            onPress={handleRefresh}
            disabled={isLoading}
            accessibilityRole="button"
            accessibilityLabel="Refresh"
          >
            <Text style={styles.refreshButtonText}>{isLoading ? 'Refreshing…' : 'Refresh'}</Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.mainCardRow}>
        <View style={styles.mainCard}>
          {error ? (
            <View style={styles.errorCard}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : null}

          <ScrollView style={styles.resultsContainer}>
            <View style={styles.tableContainer}>
              <View style={styles.tableHeader}>
                <View style={[styles.tableCell, styles.assessmentCol]}>
                  <Text style={styles.headerText}>Assessment</Text>
                </View>
                <View style={[styles.tableCell, styles.siteCol]}>
                  <Text style={styles.headerText}>Site</Text>
                </View>
                <View style={[styles.tableCell, styles.periodCol]}>
                  <Text style={styles.headerText}>Period</Text>
                </View>
                <View style={[styles.tableCell, styles.totalCol]}>
                  <Text style={[styles.headerText, styles.headerTextRight]}>Total</Text>
                </View>
              </View>

              {!hasLoaded && isLoading ? (
                <View style={styles.placeholderRow}>
                  <Text style={styles.placeholderText}>Loading usage…</Text>
                </View>
              ) : rows.length === 0 ? (
                <View style={styles.placeholderRow}>
                  <Text style={styles.placeholderText}>
                    {hasLoaded ? 'No usage data for the selected filters.' : 'Select filters above to view usage.'}
                  </Text>
                </View>
              ) : (
                rows.map((row) => (
                  row.isSpacer ? (
                    <View key={row.key} style={styles.spacerRow} />
                  ) : (
                    <View key={row.key} style={styles.tableRow}>
                      <View style={[styles.tableCell, styles.assessmentCol]}>
                        <Text style={styles.cellText}>{row.assessment}</Text>
                      </View>
                      <View style={[styles.tableCell, styles.siteCol]}>
                        <Text style={styles.cellText}>{row.site}</Text>
                      </View>
                      <View style={[styles.tableCell, styles.periodCol]}>
                        <Text style={styles.cellText}>{row.period}</Text>
                      </View>
                      <View style={[styles.tableCell, styles.totalCol]}>
                        <Text style={[styles.cellText, styles.cellTextRight]}>{row.total}</Text>
                      </View>
                    </View>
                  )
                ))
              )}
            </View>
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    padding: 20,
    backgroundColor: EDITOR_COLORS.background,
  },
  filterBarRow: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 16,
  },
  filterBar: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.background,
  },
  filterLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    marginRight: 8,
  },
  filterSpacer: {
    width: 20,
  },
  dropdownBtn: {
    height: 28,
    minWidth: 150,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
  },
  dropdownBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
  },
  dropdownBtnChevron: {
    ...editorText,
    fontSize: 11,
    color: EDITOR_COLORS.textMuted,
    marginLeft: 4,
  },
  refreshButton: {
    height: 28,
    paddingHorizontal: 16,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  refreshButtonDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  refreshButtonText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  mainCardRow: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
  },
  mainCard: {
    width: '100%',
    maxWidth: 700,
    flex: 1,
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 20,
  },
  resultsContainer: {
    flex: 1,
  },
  tableContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  spacerRow: {
    height: 14,
    backgroundColor: EDITOR_COLORS.background,
  },
  tableCell: {
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  assessmentCol: {
    flex: 2,
    minWidth: 160,
  },
  siteCol: {
    flex: 2,
    minWidth: 160,
  },
  periodCol: {
    flex: 1,
    minWidth: 100,
  },
  totalCol: {
    flex: 1,
    minWidth: 100,
    alignItems: 'flex-end',
  },
  headerText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    textDecorationLine: 'underline',
    alignSelf: 'flex-start',
  },
  // Total is numeric — right-justified rather than left-aligned like the other (textual)
  // columns. alignSelf here overrides headerText's own 'flex-start' (needed elsewhere so the
  // underline hugs the label instead of stretching full cell width).
  headerTextRight: {
    alignSelf: 'flex-end',
  },
  cellText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  cellTextRight: {
    textAlign: 'right',
  },
  placeholderRow: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  placeholderText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
});
